import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export interface AccessDeviceManagementWrite {
  command_id: string;
  device_id: string;
  gateway_id: string;
  is_active: boolean;
  slot_ids: readonly string[];
  device_type?: string | null;
  retired?: boolean;
  device_name?: string | null;
  gateway_name?: string | null;
  battery?: number | null;
  expected_revision?: number;
}

export interface AccessDeviceManagementBody {
  command_id: string;
  device_id: string;
  gateway_id: string;
  is_active: boolean;
  slot_ids: readonly string[];
  device_type: string | null;
  retired: boolean;
  device_name: string | null;
  gateway_name: string | null;
  battery: number | null;
  expected_revision?: number;
}

export interface AccessDeviceManagementAttempt {
  readonly property_id: string;
  readonly core_device_id: string | null;
  readonly body: AccessDeviceManagementBody;
}

export interface AccessDeviceManagementSnapshot {
  core_device_id: string;
  provider: 'LORA';
  device_id: string;
  gateway_id: string;
  device_type: string | null;
  is_active: boolean;
  device_name: string | null;
  gateway_name: string | null;
  battery: number | null;
  revision: number;
  group_revision: number | null;
  row_version: number;
  provider_support: 'UNVERIFIED';
  slot_ids: string[];
  retired: boolean;
  created_at: string;
  updated_at: string;
}

export interface AccessDeviceManagementReceipt {
  command_id: string;
  core_device_id: string;
  revision: number;
  effect: 'CHANGED' | 'NO_CHANGE';
  device: AccessDeviceManagementSnapshot;
}

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const exactKeys = (value: Record<string, unknown>, keys: readonly string[]) =>
  Object.keys(value).length === keys.length && keys.every((key) => key in value);
const safeInteger = (value: unknown): value is number => Number.isSafeInteger(value);
const nullableText = (value: unknown, max: number): value is string | null =>
  value === null || (typeof value === 'string' && Array.from(value).length <= max);
const nullableRevision = (value: unknown): value is number | null =>
  value === null || (safeInteger(value) && value > 0);
const int32OrNull = (value: unknown): value is number | null =>
  value === null || (safeInteger(value) && value >= -(2 ** 31) && value <= 2 ** 31 - 1);
const opaqueId = (value: unknown): value is string =>
  typeof value === 'string' &&
  Array.from(value).length >= 1 &&
  Array.from(value).length <= 50 &&
  !Array.from(value).some((char) => {
    const code = char.codePointAt(0) ?? 0;
    return code <= 31 || (code >= 127 && code <= 159);
  });
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '장비 변경 요청 또는 응답 형식이 올바르지 않습니다.',
  });

function makeAttempt(
  propertyId: string,
  coreDeviceId: string | null,
  value: AccessDeviceManagementWrite,
  registration: boolean,
) {
  const requestKeys = [
    'command_id',
    'device_id',
    'gateway_id',
    'is_active',
    'slot_ids',
    'device_type',
    'retired',
    'device_name',
    'gateway_name',
    'battery',
    'expected_revision',
  ];
  if (
    !isUuid(propertyId) ||
    (coreDeviceId !== null && !isUuid(coreDeviceId)) ||
    !record(value) ||
    !isUuid(value.command_id) ||
    Object.keys(value).some((key) => !requestKeys.includes(key)) ||
    (registration
      ? value.expected_revision !== undefined
      : !safeInteger(value.expected_revision) || value.expected_revision <= 0)
  )
    throw invalid();

  const body = {
    command_id: value.command_id,
    device_id: value.device_id,
    gateway_id: value.gateway_id,
    is_active: value.is_active,
    slot_ids: value.slot_ids,
    device_type: value.device_type ?? null,
    ...(registration ? {} : { expected_revision: value.expected_revision }),
    retired: value.retired ?? false,
    device_name: value.device_name ?? null,
    gateway_name: value.gateway_name ?? null,
    battery: value.battery ?? null,
  };
  if (
    !opaqueId(body.device_id) ||
    !opaqueId(body.gateway_id) ||
    typeof body.is_active !== 'boolean' ||
    !Array.isArray(body.slot_ids) ||
    body.slot_ids.length > 10_000 ||
    !(body.device_type === null || nullableText(body.device_type, 24)) ||
    typeof body.retired !== 'boolean' ||
    !(body.device_name === null || nullableText(body.device_name, 50)) ||
    !(body.gateway_name === null || nullableText(body.gateway_name, 50)) ||
    !int32OrNull(body.battery) ||
    (body.retired && body.slot_ids.length !== 0)
  )
    throw invalid();

  const slots: string[] = [];
  const seen = new Set<string>();
  for (let index = 0; index < body.slot_ids.length; index += 1) {
    if (!Object.hasOwn(body.slot_ids, index)) throw invalid();
    const slot: unknown = body.slot_ids[index];
    if (!isUuid(slot) || seen.has(slot.toLowerCase())) throw invalid();
    seen.add(slot.toLowerCase());
    slots.push(slot);
  }
  const frozenBody = Object.freeze({
    ...body,
    slot_ids: Object.freeze(slots),
  }) as AccessDeviceManagementBody;
  return Object.freeze({ property_id: propertyId, core_device_id: coreDeviceId, body: frozenBody });
}

function isSnapshot(
  value: unknown,
  coreDeviceId: string,
  body: AccessDeviceManagementBody,
): value is AccessDeviceManagementSnapshot {
  const keys = [
    'core_device_id',
    'provider',
    'device_id',
    'gateway_id',
    'device_type',
    'is_active',
    'device_name',
    'gateway_name',
    'battery',
    'revision',
    'group_revision',
    'row_version',
    'provider_support',
    'slot_ids',
    'retired',
    'created_at',
    'updated_at',
  ];
  if (!record(value) || !exactKeys(value, keys) || !Array.isArray(value.slot_ids)) return false;
  const slots = value.slot_ids;
  const requested = new Set(body.slot_ids.map((slot) => slot.toLowerCase()));
  const returned = new Set<string>();
  for (let index = 0; index < slots.length; index += 1) {
    if (!Object.hasOwn(slots, index)) return false;
    const slot: unknown = slots[index];
    if (!isUuid(slot) || returned.has(slot.toLowerCase()) || !requested.has(slot.toLowerCase()))
      return false;
    returned.add(slot.toLowerCase());
  }
  return (
    isUuid(value.core_device_id) &&
    value.core_device_id.toLowerCase() === coreDeviceId.toLowerCase() &&
    value.provider === 'LORA' &&
    value.device_id === body.device_id &&
    value.gateway_id === body.gateway_id &&
    value.device_type === body.device_type &&
    value.is_active === body.is_active &&
    value.device_name === body.device_name &&
    value.gateway_name === body.gateway_name &&
    value.battery === body.battery &&
    safeInteger(value.revision) &&
    value.revision > 0 &&
    nullableRevision(value.group_revision) &&
    safeInteger(value.row_version) &&
    value.row_version > 0 &&
    value.provider_support === 'UNVERIFIED' &&
    returned.size === slots.length &&
    slots.length === body.slot_ids.length &&
    typeof value.retired === 'boolean' &&
    value.retired === body.retired &&
    (value.retired ? slots.length === 0 : true) &&
    isOffsetDateTime(value.created_at) &&
    isOffsetDateTime(value.updated_at)
  );
}

function parseReceipt(
  value: unknown,
  commandId: string,
  pathDeviceId: string | null,
  body: AccessDeviceManagementBody,
): AccessDeviceManagementReceipt {
  if (
    !record(value) ||
    !exactKeys(value, ['command_id', 'core_device_id', 'revision', 'effect', 'device']) ||
    value.command_id !== commandId ||
    !isUuid(value.core_device_id) ||
    (pathDeviceId !== null && value.core_device_id.toLowerCase() !== pathDeviceId.toLowerCase()) ||
    !safeInteger(value.revision) ||
    value.revision <= 0 ||
    (value.effect !== 'CHANGED' && value.effect !== 'NO_CHANGE') ||
    !isSnapshot(value.device, value.core_device_id, body) ||
    value.device.revision !== value.revision ||
    value.device.core_device_id.toLowerCase() !== value.core_device_id.toLowerCase()
  )
    throw invalid();
  return value as unknown as AccessDeviceManagementReceipt;
}

async function send(
  client: ServiceClient,
  propertyId: string,
  coreDeviceId: string | null,
  write: AccessDeviceManagementWrite,
  signal?: AbortSignal,
): Promise<AccessDeviceManagementReceipt> {
  const registration = coreDeviceId === null;
  const attempt = makeAttempt(propertyId, coreDeviceId, write, registration);
  const path = registration
    ? `admin/properties/${propertyId}/access-devices`
    : `admin/properties/${propertyId}/access-devices/${coreDeviceId}`;
  const value = registration
    ? await client.post<unknown>(path, { json: attempt.body, signal })
    : await client.put<unknown>(path, { json: attempt.body, signal });
  return parseReceipt(value, attempt.body.command_id, coreDeviceId, attempt.body);
}

/** Register with a caller command UUID and let Core issue the device UUID. */
export function postAccessDeviceManagement(
  client: ServiceClient,
  propertyId: string,
  write: AccessDeviceManagementWrite,
  signal?: AbortSignal,
): Promise<AccessDeviceManagementReceipt> {
  return send(client, propertyId, null, write, signal);
}

/** Replace one existing Core device using a positive current CAS revision. */
export function putAccessDeviceManagement(
  client: ServiceClient,
  propertyId: string,
  coreDeviceId: string,
  write: AccessDeviceManagementWrite,
  signal?: AbortSignal,
): Promise<AccessDeviceManagementReceipt> {
  return send(client, propertyId, coreDeviceId, write, signal);
}
